// src/screens/StandingOrders.tsx
// ─────────────────────────────────────────────────────────────────────────────
// M7 — Standing Orders summary.
// Auto-generates standing orders from plan decisions (Brief 6).
// Adviser reviews with client, can add/remove custom orders.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { ClientData, StandingOrder } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { generateStandingOrders } from '../utils/planCalculations';
import Scoreboard from '../components/Scoreboard';
import { useCommaInput } from '../utils/useCommaInput';
import { downloadSession1Summary } from '../utils/downloadSession1PDF';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function StandingOrders({ data, onUpdate }: Props) {
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customAmount, setCustomAmount] = useState(0);
  const [customRecipient, setCustomRecipient] = useState('');

  const customAmountInput = useCommaInput(customAmount, setCustomAmount);

  // Auto-generate on mount if empty
  const syncOrders = useCallback(() => {
    if (data.standingOrders.length === 0) {
      const generated = generateStandingOrders(data);
      if (generated.length > 0) {
        onUpdate({ standingOrders: generated });
      }
    }
  }, [data, onUpdate]);

  useEffect(() => {
    syncOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orders = data.standingOrders;
  const totalMonthly = orders.reduce((sum, o) => sum + o.amount, 0);

  const handleAddCustom = () => {
    if (!customLabel || customAmount <= 0) return;
    const newOrder: StandingOrder = {
      label: customLabel,
      amount: customAmount,
      recipient: customRecipient || 'TBC',
      startDate: 'Next month',
    };
    onUpdate({ standingOrders: [...orders, newOrder] });
    setCustomLabel('');
    setCustomAmount(0);
    setCustomRecipient('');
  };

  const handleRemove = (index: number) => {
    const updated = orders.filter((_, i) => i !== index);
    onUpdate({ standingOrders: updated });
  };

  const handleRegenerate = () => {
    const generated = generateStandingOrders(data);
    onUpdate({ standingOrders: generated });
  };

  return (
    <div className="flex flex-col h-full">
      <Scoreboard data={data} />

      <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
          <h2 className="text-lg font-bold text-ma-navy mb-1">Standing Orders to Set Up</h2>
          <p className="text-sm text-blue-600">
            These are the standing orders from your plan. Review with {data.clientName || 'the client'} and confirm.
          </p>
        </div>

        {/* Orders table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
          <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200 px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <div className="col-span-3">Payment</div>
            <div className="col-span-3 text-right">Amount</div>
            <div className="col-span-4">Why / when complete</div>
            <div className="col-span-1">Start</div>
            <div className="col-span-1"></div>
          </div>

          {orders.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-gray-400 text-sm mb-4">No standing orders yet.</p>
              <button
                onClick={handleRegenerate}
                className="bg-ma-navy text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-ma-navy-light transition-colors"
              >
                Auto-generate from plan
              </button>
            </div>
          ) : (
            orders.map((order, idx) => (
              <div key={idx} className="grid grid-cols-12 px-4 py-3 border-b border-gray-100 items-center hover:bg-gray-50 transition-colors">
                <div className="col-span-3">
                  <div className="text-sm font-medium text-gray-800">{order.label}</div>
                </div>
                <div className="col-span-3 text-right text-sm font-bold text-ma-navy">
                  {fmt(order.amount)}<span className="font-normal text-xs text-gray-400 ml-1">/mo</span>
                </div>
                <div className="col-span-4 text-xs text-gray-500">
                  {order.note || '—'}
                  {order.endDate && <span className="text-amber-500 ml-1">Until {(() => {
                    try {
                      const d = new Date(order.endDate);
                      if (!isNaN(d.getTime()) && order.endDate.includes('T')) {
                        const yearsAway = d.getFullYear() - new Date().getFullYear();
                        return `age ${(data.clientAge || 40) + yearsAway}`;
                      }
                      return order.endDate;
                    } catch { return order.endDate; }
                  })()}</span>}
                </div>
                <div className="col-span-1 text-xs text-gray-500">{order.startDate}</div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => handleRemove(idx)}
                    className="text-red-400 hover:text-red-600 text-xs"
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Total row */}
          {orders.length > 0 && (
            <div className="grid grid-cols-12 px-4 py-3 bg-ma-navy/5 items-center">
              <div className="col-span-3 text-sm font-bold text-ma-navy">Total monthly</div>
              <div className="col-span-3 text-right text-lg font-bold text-ma-navy">{fmt(totalMonthly)}</div>
              <div className="col-span-6"></div>
            </div>
          )}
        </div>

        {/* Email + PDF buttons */}
        {orders.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-3">
            <a
              href={`mailto:${encodeURIComponent(data.clientEmail || '')}?subject=${encodeURIComponent(
                `Your Session 1 Standing Orders — Motion Accountancy`
              )}&body=${encodeURIComponent(
                orders.map(o => `${o.label}: ${fmt(o.amount)}/month to ${o.recipient || 'TBC'}${o.note ? ` — ${o.note}` : ''}`).join('\n')
                + `\n\nTotal: ${fmt(totalMonthly)}/month`
              )}`}
              className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-100 transition-colors"
            >
              📧 Email standing orders to {data.clientName || 'client'}
            </a>
            <button
              onClick={() => downloadSession1Summary(data)}
              className="inline-flex items-center gap-2 bg-ma-navy text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-ma-navy-light transition-colors"
            >
              📄 Download Session 1 Summary PDF
            </button>
          </div>
        )}

        {/* Action buttons */}
        {orders.length > 0 && (
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setShowCustomForm(true)}
              className="border-2 border-dashed border-gray-300 text-gray-500 rounded-xl px-4 py-2.5 text-sm font-medium hover:border-gray-400 hover:text-gray-600 transition-colors"
            >
              + Add custom order
            </button>
            <button
              onClick={handleRegenerate}
              className="bg-gray-100 text-gray-600 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-gray-200 transition-colors ml-auto"
            >
              Re-generate from plan
            </button>
          </div>
        )}

        {/* Add custom — collapsible */}
        {showCustomForm && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6 animate-[fadeIn_0.2s_ease-in]">
            <h3 className="text-sm font-bold text-ma-navy mb-3">Add custom standing order</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Label</label>
                <input
                  type="text"
                  value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)}
                  className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ma-navy/20"
                  placeholder="e.g. Life insurance"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Monthly amount</label>
                <div className="flex items-center mt-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-ma-navy/20">
                  <span className="text-gray-400 text-sm mr-1">£</span>
                  <input
                    {...customAmountInput}
                    className="flex-1 bg-transparent text-sm outline-none"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Recipient</label>
                <input
                  type="text"
                  value={customRecipient}
                  onChange={e => setCustomRecipient(e.target.value)}
                  className="w-full mt-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ma-navy/20"
                  placeholder="e.g. Aviva"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => { handleAddCustom(); setShowCustomForm(false); }}
                disabled={!customLabel || customAmount <= 0}
                className="bg-ma-navy text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-ma-navy-light disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Add
              </button>
              <button
                onClick={() => setShowCustomForm(false)}
                className="bg-gray-100 text-gray-600 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
